// A short list of the answers most drivers give, with room for the ones who
// give something else.
//
// The list is a shortcut, never a restriction: "Other" opens a free text field
// and whatever is typed there is the value, so an unusual job title is one tap
// further away rather than impossible.

import { useEffect, useRef, useState } from 'react'

export interface TitleChipsProps {
  label: string
  options: readonly string[]
  value: string | null
  onChange: (value: string | null) => void
  required?: boolean
  error?: string
  /** Placeholder for the free-text field behind "Other". */
  otherPlaceholder?: string
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

const chip = (selected: boolean) =>
  join(
    'rounded-[7px] border px-2.5 py-1.5 text-[11px]',
    selected
      ? 'border-doubleNickel-brand-500 bg-doubleNickel-brand-25 font-semibold text-doubleNickel-brand-700'
      : 'border-doubleNickel-gray-300 bg-white font-medium text-doubleNickel-gray-700'
  )

export const TitleChips = ({
  label,
  options,
  value,
  onChange,
  required = false,
  error,
  otherPlaceholder = 'Enter your job title',
}: TitleChipsProps) => {
  // A value that is not in the list can only have come from the free field
  const startsOther = value != null && value !== '' && !options.includes(value)
  const [showOther, setShowOther] = useState(startsOther)
  const otherRef = useRef<HTMLInputElement | null>(null)
  // Opened by a tap, as opposed to opened because the stored value is a custom
  // one. Only the first earns focus; the second would pull the keyboard up the
  // moment the sheet appears, which is the behaviour we set out to avoid.
  const openedByTap = useRef(false)

  useEffect(() => {
    if (showOther && openedByTap.current) otherRef.current?.focus()
  }, [showOther])

  return (
    <div>
      <div className="mb-0.5 text-[10px] font-medium text-doubleNickel-gray-600">
        {label}
        {required && (
          <span className="ml-0.5 text-doubleNickel-error-600">*</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => {
              setShowOther(false)
              onChange(option)
            }}
            className={chip(value === option)}
          >
            {option}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={showOther}
          onClick={() => {
            openedByTap.current = true
            setShowOther(true)
            onChange(null)
          }}
          className={chip(showOther)}
        >
          Other
        </button>
      </div>
      {showOther && (
        <input
          type="text"
          ref={otherRef}
          aria-label={`${label} — other`}
          value={startsOther ? (value as string) : ''}
          placeholder={otherPlaceholder}
          onChange={(event) => onChange(event.target.value || null)}
          className="mt-2 w-full border-b border-doubleNickel-gray-300 bg-transparent py-1 text-[12.5px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400"
        />
      )}
      {error && (
        <div className="mt-1 text-[10px] text-doubleNickel-error-700">
          {error}
        </div>
      )}
    </div>
  )
}
